/**
 * Payment Provider Types
 * 
 * Standardized types for payment method integration across all providers
 * (Bank Transfer, Stripe, SumUp, SlickPay, and future providers)
 */

/**
 * Payment method types supported by the platform
 */
export type PaymentMethodType = 'bank_transfer' | 'stripe' | 'sumup' | 'slickpay';

/**
 * Payment provider configuration
 * Defines the capabilities and requirements of each payment provider
 */
export interface PaymentProvider {
  /** Unique identifier for the payment method */
  id: PaymentMethodType;
  
  /** Internal name for the provider */
  name: string;
  
  /** Display name shown to users */
  displayName: string;
  
  /** Whether this provider is enabled at the platform level */
  enabled: boolean;
  
  /** Whether this provider requires an API key to function */
  requiresApiKey: boolean;
  
  /** Whether this provider supports webhooks for payment confirmation */
  supportsWebhooks: boolean;
  
  /** Whether this provider uses redirect-based payment flow */
  usesRedirect: boolean;
  
  /** Icon name or component to display */
  icon?: string;
  
  /** Description of the payment method */
  description?: string;
}

/**
 * Payment method configuration at unit level
 */
export interface PaymentMethodConfig {
  /** Whether this method is enabled for the unit */
  enabled: boolean;
  
  /** Whether the unit has completed configuration (e.g., provided API key, bank details) */
  configured?: boolean;
  
  /** Optional transaction fee override for this unit (overrides platform default) */
  transactionFee?: number;
}

/**
 * System-level payment settings
 * Configured by platform administrators
 */
export interface SystemPaymentSettings {
  /** Bank transfer settings */
  bankTransfer: {
    /** Whether bank transfer is enabled platform-wide */
    enabled: boolean;
    
    /** Transaction fee for bank transfers (typically £0) */
    transactionFee: number;
  };
  
  /** Stripe settings */
  stripe: {
    /** Whether Stripe is enabled platform-wide */
    enabled: boolean;
    
    /** Transaction fee for Stripe payments */
    transactionFee: number;
  };
  
  /** SumUp settings */
  sumup: {
    /** Whether SumUp is enabled platform-wide */
    enabled: boolean;
    
    /** Transaction fee for SumUp payments */
    transactionFee: number;
  };

  /** SlickPay settings */
  slickpay: {
    /** Whether SlickPay is enabled platform-wide */
    enabled: boolean;

    /** Transaction fee for SlickPay payments */
    transactionFee: number;
  };
}

/**
 * Payment history entry
 * Unified structure for all payment methods
 */
export interface PaymentHistoryEntry {
  /** Amount paid */
  amount: number;
  
  /** Date of payment */
  date: Date;
  
  /** Payment method used */
  method: PaymentMethodType;
  
  /** Universal transaction ID (provider-specific format) */
  transactionId?: string;
  
  /** Processing fee charged */
  processingFee: number;
  
  /** Subtotal (before fees and VAT) */
  subtotal?: number;
  
  /** VAT amount */
  vatAmount?: number;
  
  /** Transaction fee amount */
  transactionFee?: number;
  
  /** Accounting split for this payment */
  accountingSplit?: {
    lodgeTransfer: number;
    platformProfit: number;
  };
  
  // Provider-specific fields
  
  /** Stripe Payment Intent ID */
  stripePaymentIntentId?: string;
  
  /** Stripe Charge ID */
  stripeChargeId?: string;
  
  /** SumUp Checkout ID */
  sumupCheckoutId?: string;
  
  /** SumUp Transaction Code */
  sumupTransactionCode?: string;

  /** SlickPay invoice ID (provider numeric or string id) */
  slickpayInvoiceId?: string;
}

/**
 * Bank transfer details
 */
export interface BankTransferDetails {
  /** Name on the bank account */
  nameOnAccount?: string;
  
  /** Bank sort code (UK format: 12-34-56) */
  sortCode?: string;
  
  /** Bank account number */
  accountNumber?: string;
}

/**
 * SumUp configuration
 */
export interface SumUpConfig {
  /** Whether SumUp is enabled for this unit */
  enabled: boolean;
  
  /** Encrypted API key (never exposed to frontend) */
  apiKeyEncrypted?: string;
  
  /** Last 4 characters of API key for display purposes */
  apiKeyLastFour?: string;
  
  /** Merchant code (if applicable) */
  merchantCode?: string;
  
  /** Environment: 'production' or 'sandbox' */
  environment?: 'production' | 'sandbox';
}

/**
 * SlickPay configuration (Hope Macy PIS API v1 — platform credentials in GSM,
 * no per-unit API keys). Unit-level enablement is gated on Open Banking bank
 * details in settings.paymentMethods.slickpay (legacy: bankTransfer fallback)
 * and on Hope Macy verifying the account (registration).
 */
export interface SlickPayConfig {
  /** Whether SlickPay is enabled for this unit */
  enabled: boolean;

  /**
   * Whether the unit has Open Banking bank details configured.
   * Derived from slickpay.nameOnAccount + sortCode + accountNumber (with legacy bankTransfer fallback).
   * @deprecated apiKeyEncrypted / apiKeyLastFour are no longer used for SlickPay.
   */
  configured?: boolean;

  /** Creditor account name for Open Banking settlements */
  nameOnAccount?: string;

  /** Creditor sort code (UK format: 12-34-56) */
  sortCode?: string;

  /** Creditor account number */
  accountNumber?: string;

  /**
   * Hope Macy verification, written only by Cloud Functions. Pending until the account appears in
   * GET /creditor-accounts; once active the bank details are locked. Absent for units that set up
   * SlickPay before verification existed until the registration sync backfills them.
   */
  registration?: SlickPayRegistration;
}

export interface SlickPayRegistration {
  status: 'pending' | 'active';
  /** 14 digits (sort code + account number) the request or verification is for */
  bankAccountId?: string;
  /** Hope Macy creditor account UUID, set once verified */
  creditorAccountId?: string;
  source?: 'unit' | 'backfill';
  /** uid of the officer who submitted the details */
  requestedBy?: string;
  /** Firestore Timestamps */
  requestedAt?: unknown;
  activatedAt?: unknown;
  lastCheckedAt?: unknown;
  /** Outcome of asking Hope Macy to verify the account */
  submission?: {
    status: 'awaitingApi' | 'submitted' | 'failed';
    error?: string;
    updatedAt?: unknown;
  };
}

/**
 * Resolve creditor bank details for SlickPay Open Banking.
 * Prefers slickpay-specific fields; falls back to bankTransfer for legacy units.
 */
export function resolveSlickPayBankDetails(
  paymentMethods?: UnitPaymentMethods
): BankTransferDetails {
  const slick = paymentMethods?.slickpay;
  const bank = paymentMethods?.bankTransfer;

  return {
    nameOnAccount: (slick?.nameOnAccount ?? bank?.nameOnAccount ?? '').trim(),
    sortCode: (slick?.sortCode ?? bank?.sortCode ?? '').trim(),
    accountNumber: (slick?.accountNumber ?? bank?.accountNumber ?? '').trim(),
  };
}

/** True when name, sort code and account number are all present for SlickPay. */
export function isSlickPayBankDetailsConfigured(paymentMethods?: UnitPaymentMethods): boolean {
  const { nameOnAccount, sortCode, accountNumber } = resolveSlickPayBankDetails(paymentMethods);
  return !!(nameOnAccount && sortCode && accountNumber);
}

/** True when guests can pay by SlickPay: switched on, bank details present, and not awaiting Hope Macy verification. */
export function isSlickPayReadyForPayments(paymentMethods?: UnitPaymentMethods): boolean {
  const slickpay = paymentMethods?.slickpay;
  return (
    !!slickpay?.enabled &&
    slickpay.registration?.status !== 'pending' &&
    isSlickPayBankDetailsConfigured(paymentMethods)
  );
}

/**
 * Stripe configuration
 */
export interface StripeConfig {
  /** Whether Stripe is enabled for this unit */
  enabled: boolean;
  
  /** Stripe Connected Account ID (stored at unit root level, not in paymentMethods) */
  // Note: stripeConnectedAccountId is stored separately on the Unit document
}

/**
 * Unit payment methods configuration
 */
export interface UnitPaymentMethods {
  /** Bank transfer configuration */
  bankTransfer?: BankTransferDetails & { enabled: boolean };
  
  /** Stripe configuration */
  stripe?: StripeConfig;
  
  /** SumUp configuration */
  sumup?: SumUpConfig;

  /** SlickPay configuration */
  slickpay?: SlickPayConfig;
}

/**
 * Payment method validation result
 */
export interface PaymentMethodValidationResult {
  /** Whether the validation was successful */
  valid: boolean;
  
  /** Error message if validation failed */
  error?: string;
  
  /** Additional validation details */
  details?: Record<string, any>;
}

/**
 * Checkout session creation parameters
 */
export interface CreateCheckoutParams {
  /** Unit ID */
  unitId: string;
  
  /** Event ID */
  eventId: string;
  
  /** Invoice ID */
  invoiceId: string;
  
  /** Payment amount */
  amount: number;
  
  /** Processing fee to apply */
  processingFee: number;
  
  /** Payment method to use */
  paymentMethod: PaymentMethodType;
}

/**
 * Checkout session result
 */
export interface CheckoutSessionResult {
  /** Checkout session ID */
  sessionId: string;
  
  /** URL to redirect user to for payment */
  url: string;
  
  /** Total amount (including fees) */
  totalAmount: number;
  
  /** Processing fee */
  processingFee: number;
  
  /** Expiry time for the checkout session */
  expiresAt?: Date;
}
